import { useState, useEffect } from "react";
import { ExternalLink, Clock, RefreshCw, Newspaper, ArrowRight, Search, Filter, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// Format relative time
const formatTimeAgo = (dateString) => {
  const now = new Date();
  const date = new Date(dateString);
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
};

// Placeholder for failed images
const PLACEHOLDER = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='225' viewBox='0 0 400 225'%3E%3Crect fill='%23001a40' width='400' height='225'/%3E%3Ctext fill='%233B82F6' font-family='Arial' font-size='24' x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle'%3ECRYPTO NEWS%3C/text%3E%3C/svg%3E";

// Featured Hero Card
const HeroCard = ({ article }) => {
  const [imgError, setImgError] = useState(false);
  
  if (!article) return null;
  
  return (
    <a
      href={article.url}
      target="_blank"
      rel="noopener noreferrer"
      className="group relative block rounded-2xl overflow-hidden h-[400px] sm:h-[500px]"
      data-testid="news-hero"
    >
      {/* Background Image */}
      <img
        src={imgError ? PLACEHOLDER : (article.image || PLACEHOLDER)}
        alt={article.title}
        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
        onError={() => setImgError(true)}
      />
      
      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#001435] via-[#001435]/70 to-transparent" />
      
      {/* Content */}
      <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-10">
        <div className="flex items-center gap-3 mb-4">
          <span className="bg-[#3B82F6] text-white text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wide">
            Featured
          </span>
          <span className="bg-white/10 backdrop-blur-sm text-white text-xs px-3 py-1.5 rounded-full flex items-center gap-1.5">
            <Clock className="w-3 h-3" />
            {formatTimeAgo(article.published_at)}
          </span>
        </div>
        
        <h1 className="text-2xl sm:text-4xl font-bold text-white mb-4 leading-tight group-hover:text-[#3B82F6] transition-colors">
          {article.title}
        </h1>
        
        <p className="text-blue-100/70 text-sm sm:text-base mb-6 line-clamp-2 max-w-3xl">
          {article.snippet}
        </p>
        
        <div className="flex items-center gap-4">
          <span className="text-white/60 text-sm font-medium">{article.source}</span>
          <span className="flex items-center gap-2 text-[#3B82F6] font-semibold group-hover:gap-3 transition-all">
            Read Full Article <ArrowRight className="w-5 h-5" />
          </span>
        </div>
      </div>
    </a>
  );
};

// Regular News Card
const NewsCard = ({ article, size = "normal" }) => {
  const [imgError, setImgError] = useState(false);
  
  const isLarge = size === "large";
  
  return (
    <a
      href={article.url}
      target="_blank"
      rel="noopener noreferrer"
      className={`group block bg-[#001a40]/50 rounded-xl overflow-hidden border border-white/5 hover:border-[#3B82F6]/30 hover:bg-[#001a40] transition-all duration-300 ${isLarge ? 'md:col-span-2' : ''}`}
      data-testid={`news-card-${article.id}`}
    >
      <div className={`relative overflow-hidden ${isLarge ? 'h-48 sm:h-56' : 'h-40 sm:h-44'}`}>
        <img
          src={imgError ? PLACEHOLDER : (article.image || PLACEHOLDER)}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          onError={() => setImgError(true)}
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#001435] to-transparent" />
        
        <div className="absolute top-3 right-3 bg-black/50 backdrop-blur-sm text-white/80 text-[10px] px-2 py-1 rounded flex items-center gap-1">
          <Clock className="w-2.5 h-2.5" />
          {formatTimeAgo(article.published_at)}
        </div>
        
        {article.categories && article.categories[0] && (
          <div className="absolute top-3 left-3 bg-[#3B82F6]/80 backdrop-blur-sm text-white text-[10px] font-semibold px-2 py-1 rounded uppercase">
            {article.categories[0]}
          </div>
        )}
      </div>
      
      <div className="p-4 sm:p-5">
        <h3 className={`text-white font-semibold leading-snug mb-3 group-hover:text-[#3B82F6] transition-colors line-clamp-2 ${isLarge ? 'text-lg' : 'text-sm sm:text-base'}`}>
          {article.title}
        </h3>
        
        {isLarge && (
          <p className="text-blue-200/50 text-sm line-clamp-2 mb-3 hidden sm:block">
            {article.snippet}
          </p>
        )}
        
        <div className="flex items-center justify-between">
          <span className="text-blue-200/50 text-xs font-medium">{article.source}</span>
          <ExternalLink className="w-4 h-4 text-[#3B82F6] opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </div>
    </a>
  );
};

// Mobile News Item
const MobileNewsItem = ({ article }) => {
  const [imgError, setImgError] = useState(false);
  
  return (
    <a
      href={article.url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex gap-4 py-4 border-b border-white/5 last:border-b-0 group"
    >
      <div className="w-24 h-20 flex-shrink-0 rounded-lg overflow-hidden bg-[#001a40]">
        <img
          src={imgError ? PLACEHOLDER : (article.image || PLACEHOLDER)}
          alt={article.title}
          className="w-full h-full object-cover"
          onError={() => setImgError(true)}
          loading="lazy"
        />
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-white text-sm font-semibold line-clamp-2 group-hover:text-[#3B82F6] transition-colors mb-1">
          {article.title}
        </h3>
        <div className="flex items-center gap-2 text-[11px] text-blue-200/50">
          <span className="font-medium">{article.source}</span>
          <span>•</span>
          <span>{formatTimeAgo(article.published_at)}</span>
        </div>
      </div>
    </a>
  );
};

// Loading Skeletons
const HeroSkeleton = () => (
  <div className="rounded-2xl overflow-hidden h-[400px] sm:h-[500px] bg-[#001a40] animate-pulse">
    <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-10 space-y-4">
      <div className="flex gap-3">
        <div className="h-7 w-24 bg-white/10 rounded-full" />
        <div className="h-7 w-20 bg-white/10 rounded-full" />
      </div>
      <div className="h-10 w-3/4 bg-white/10 rounded" />
      <div className="h-6 w-1/2 bg-white/5 rounded" />
    </div>
  </div>
);

const CardSkeleton = ({ large = false }) => (
  <div className={`bg-[#001a40]/50 rounded-xl overflow-hidden border border-white/5 animate-pulse ${large ? 'md:col-span-2' : ''}`}>
    <div className={`bg-white/5 ${large ? 'h-48 sm:h-56' : 'h-40 sm:h-44'}`} />
    <div className="p-4 sm:p-5 space-y-3">
      <div className="h-5 bg-white/10 rounded w-full" />
      <div className="h-5 bg-white/10 rounded w-3/4" />
      <div className="h-4 bg-white/5 rounded w-1/3" />
    </div>
  </div>
);

export default function CryptoNewsPage() {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    fetchNews();
    const interval = setInterval(fetchNews, 300000);
    return () => clearInterval(interval);
  }, []);

  const fetchNews = async () => {
    try {
      // Use AbortController to handle timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000); // 8 second timeout
      
      const response = await axios.get(`${API}/api/crypto/news?limit=50`, {
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      
      setNews(response.data.news || []);
      setLastUpdated(response.data.last_updated);
    } catch (error) {
      if (error.name !== 'CanceledError') {
        console.error("Error fetching crypto news:", error);
      }
    } finally {
      setLoading(false);
    }
  };

  const heroArticle = news[0];
  const topArticles = news.slice(1, 5);
  const moreArticles = news.slice(5);

  return (
    <div className="min-h-screen bg-[#001435]" data-testid="crypto-news-page">
      {/* Header */}
      <section className="pt-8 sm:pt-12 pb-6 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-[#3B82F6]/20 flex items-center justify-center">
                <Newspaper className="w-6 h-6 sm:w-7 sm:h-7 text-[#3B82F6]" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-white">Crypto News</h1>
                <p className="text-blue-200/50 text-sm">Latest cryptocurrency news & market updates</p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Link
                to="/crypto"
                className="flex items-center gap-2 bg-[#3B82F6] hover:bg-[#2563EB] text-white font-medium px-4 py-2 rounded-lg text-sm transition-colors"
              >
                <TrendingUp className="w-4 h-4" />
                View Prices
              </Link>
              <div className="flex items-center gap-2 text-blue-200/40 text-xs">
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                <span className="hidden sm:inline">Auto-refreshes every 5 min</span>
              </div>
            </div>
          </div>

          {/* Hero Article */}
          {loading ? <HeroSkeleton /> : <HeroCard article={heroArticle} />}
        </div>
      </section>

      {/* Top Stories Grid */}
      <section className="py-8 sm:py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
            <span className="w-1 h-6 bg-[#3B82F6] rounded-full"></span>
            Top Stories
          </h2>
          
          {/* Desktop Grid */}
          <div className="hidden md:grid md:grid-cols-4 gap-5">
            {loading ? (
              <>
                <CardSkeleton large />
                <CardSkeleton />
                <CardSkeleton />
              </>
            ) : (
              <>
                {topArticles[0] && <NewsCard article={topArticles[0]} size="large" />}
                {topArticles.slice(1, 3).map((article) => (
                  <NewsCard key={article.id} article={article} />
                ))}
              </>
            )}
          </div>
          
          {/* Mobile List */}
          <div className="md:hidden bg-[#001a40]/30 rounded-xl p-4 border border-white/5">
            {loading ? (
              <div className="space-y-4 animate-pulse">
                {[1,2,3,4].map(i => (
                  <div key={i} className="flex gap-4 py-3">
                    <div className="w-24 h-20 bg-white/5 rounded-lg" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-white/10 rounded w-full" />
                      <div className="h-4 bg-white/10 rounded w-3/4" />
                      <div className="h-3 bg-white/5 rounded w-1/3" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              topArticles.map((article) => (
                <MobileNewsItem key={article.id} article={article} />
              ))
            )}
          </div>
        </div>
      </section>

      {/* More News */}
      {moreArticles.length > 0 && (
        <section className="py-8 sm:py-12 px-4 bg-[#001020]">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
              <span className="w-1 h-6 bg-[#3B82F6] rounded-full"></span>
              More News
            </h2>
            
            {/* Desktop Grid */}
            <div className="hidden md:grid md:grid-cols-3 lg:grid-cols-4 gap-5">
              {loading ? (
                <>
                  <CardSkeleton />
                  <CardSkeleton />
                  <CardSkeleton />
                  <CardSkeleton />
                </>
              ) : (
                moreArticles.map((article) => (
                  <NewsCard key={article.id} article={article} />
                ))
              )}
            </div>
            
            {/* Mobile List */}
            <div className="md:hidden bg-[#001a40]/30 rounded-xl p-4 border border-white/5">
              {loading ? (
                <div className="space-y-4 animate-pulse">
                  {[1,2,3,4,5].map(i => (
                    <div key={i} className="flex gap-4 py-3">
                      <div className="w-24 h-20 bg-white/5 rounded-lg" />
                      <div className="flex-1 space-y-2">
                        <div className="h-4 bg-white/10 rounded w-full" />
                        <div className="h-4 bg-white/10 rounded w-3/4" />
                        <div className="h-3 bg-white/5 rounded w-1/3" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                moreArticles.slice(0, 10).map((article) => (
                  <MobileNewsItem key={article.id} article={article} />
                ))
              )}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-12 sm:py-16 px-4 bg-gradient-to-r from-[#001435] to-[#002060]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            Ready to Trade Crypto?
          </h2>
          <p className="text-blue-200/60 mb-8">
            Visit FonderX in New Westminster for competitive rates and instant settlement.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/crypto"
              className="inline-flex items-center justify-center gap-2 bg-[#3B82F6] hover:bg-[#2563EB] text-white font-semibold px-6 py-3 rounded-lg transition-colors"
            >
              <TrendingUp className="w-5 h-5" />
              View Live Prices
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
            >
              Contact Us
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
